import styled from 'styled-components'
import Linkify from 'react-linkify'
import { NO_BREAK_SPACE, StringUtils } from 'src/utils/string'
import { useMentionUtils } from 'src/components/commentInput/hooks'
import { MentionProps } from 'src/hook/usePosts'
import { useEffect, useRef } from 'react'
import { UserProfileUtils, useUserProfile } from 'src/context/userProfileContext'
import { useTrackEvents } from 'src/context/userProfileContext/hooks'
import { ComponentName } from 'src/utils/datahubDefaultEvent'

export type PostInfoProps = {
  title: string
  description?: string
  mentions?: MentionProps[]
}

const TextPost = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow-wrap: break-word;
`

TextPost.displayName = 'TextPost'

const TitlePost = styled.h2`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow-wrap: break-word;
`

TitlePost.displayName = 'TitlePost'

const PostInfo = ({ title, description, mentions }: PostInfoProps) => {
  const container = useRef<HTMLDivElement>(null)
  const { convertMentionToHTML } = useMentionUtils()
  const { loggedUserUcode, onOpen: onOpenUserProfile } = useUserProfile()
  const { sendOpenProfileEvent } = useTrackEvents({ loggedUserUcode })

  const descriptionWithMentions = convertMentionToHTML({
    text: description as string,
    mentions: mentions?.map((mention) => ({
      tag: mention?.tag,
      failed: Boolean(mention?.failed),
      failureReason: mention?.failureReason,
      ucode: mention?.metadata?.ucode,
      name: mention?.metadata?.name
    }))
  })

  useEffect(() => {
    const elementsToOpenProfile = container.current?.querySelectorAll(
      '[data-action="open-profile"]'
    )

    const callback = ({ customEvent, ucode }) => {
      onOpenUserProfile(customEvent, ucode)
      sendOpenProfileEvent({
        componentName: ComponentName.POST_FEED,
        userUcode: ucode,
        accessFromMention: true
      })
    }

    const removeListener = UserProfileUtils.openEventListener({
      elements: elementsToOpenProfile,
      componentName: ComponentName.POST_FEED,
      callback
    })

    return removeListener
  }, [])

  return (
    <div
      ref={container}
      className="_d_flex _align-items-center _justify-content-start _overflow-x-hidden"
    >
      <TitlePost className="_font-weight-bold _text-gray-900 _text-2 _mb-0">{title}</TitlePost>

      {description && (
        <TextPost className="_text-gray-900">
          <Linkify
            // @ts-ignore
            className="_text-left"
            componentDecorator={(decoratedHref, decoratedText, key) => (
              <a target="blank" href={decoratedHref} key={key} onClick={(e) => e.stopPropagation()}>
                {decoratedText}
              </a>
            )}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: StringUtils.replaceCharacterGloballyByKey(
                  descriptionWithMentions,
                  NO_BREAK_SPACE,
                  ' '
                )
              }}
            />
          </Linkify>
        </TextPost>
      )}
    </div>
  )
}

export default PostInfo
